import styled from '@emotion/native';
import React from 'react';
import { Linking, TouchableOpacity } from 'react-native';
import { green, mintGreen, sand } from '../styles/colors';
import { BodyText } from './StyledText';
import { IsDesktopProps } from '../styles/types';
import useIsDesktop from '../hooks/useIsDesktop';
import { useWebHoverToggle } from '../hooks/useWebHoverToggle';
import { applyToBeStylist } from '../utils';
import type { Href } from 'expo-router';
import { usePathname, useRouter } from 'expo-router';
import { ROUTES } from '../constants/routes';
import { EMME_SUPPORT_EMAIL } from '../constants/utility';
import { FONT_STYLES } from '../styles/constants';

function normalizePathForMatch(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  if (trimmed.length === 0) {
    return '/';
  }
  return trimmed;
}

function hrefToPathString(href: Href): string {
  if (typeof href === 'string') {
    return href;
  }
  return href.pathname;
}

function footerNavLinkColor(
  isEventLanding: boolean,
  isHovered: boolean | undefined,
): string {
  if (isEventLanding) {
    if (isHovered) {
      return mintGreen;
    }
    return green;
  }
  if (isHovered) {
    return sand;
  }
  return mintGreen;
}

interface FooterNavLinkProps {
  isDesktop: boolean;
  isCurrent: boolean;
  isEventLanding: boolean;
  onPress: () => void;
  accessibilityLabel: string;
  useWelcomeHelveticaNeue: boolean;
  children: React.ReactNode;
}

const FooterNavLink = ({
  isDesktop,
  isCurrent,
  isEventLanding,
  onPress,
  accessibilityLabel,
  useWelcomeHelveticaNeue,
  children,
}: FooterNavLinkProps) => {
  const { isHovered, webHoverProps } = useWebHoverToggle();
  return (
    <FooterLink
      isDesktop={isDesktop}
      onPress={onPress}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected: isCurrent }}
      {...webHoverProps}
    >
      <FooterNavText
        isCurrent={isCurrent}
        isHovered={isHovered}
        isEventLanding={isEventLanding}
        useWelcomeHelveticaNeue={useWelcomeHelveticaNeue}
      >
        {children}
      </FooterNavText>
    </FooterLink>
  );
};

interface FooterSocialIconButtonProps {
  isDesktop: boolean;
  onPress: () => void;
  accessibilityLabel: string;
  children: React.ReactNode;
}

const FooterSocialIconButton = ({
  isDesktop,
  onPress,
  accessibilityLabel,
  children,
}: FooterSocialIconButtonProps) => {
  const { isHovered, webHoverProps } = useWebHoverToggle();
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
      style={isHovered ? { opacity: 0.88 } : undefined}
      {...webHoverProps}
    >
      <SocialIconWrap isDesktop={isDesktop}>{children}</SocialIconWrap>
    </TouchableOpacity>
  );
};

interface FooterProps {
  isEventLanding?: boolean;
}

const Footer: React.FC<FooterProps> = ({ isEventLanding = false }) => {
  const isDesktop = useIsDesktop();
  const router = useRouter();
  const pathname = usePathname();
  const activePath = normalizePathForMatch(pathname);

  const isRouteActive = (href: Href): boolean => {
    return activePath === normalizePathForMatch(hrefToPathString(href));
  };

  const isHomeActive = isRouteActive(ROUTES.WELCOME.path);
  const isTermsActive = isRouteActive(ROUTES.TERMS.path);
  const isPrivacyActive = isRouteActive(ROUTES.PRIVACY.path);

  const footerNavLinks = (
    <>
      <FooterNavLink
        isDesktop={isDesktop}
        isCurrent={isHomeActive}
        isEventLanding={isEventLanding}
        onPress={() => router.push(ROUTES.WELCOME.path)}
        accessibilityLabel="Go to home"
        useWelcomeHelveticaNeue={isHomeActive}
      >
        Home
      </FooterNavLink>
      <LinkSeparator accessible={false}>
        <SeparatorText useWelcomeHelveticaNeue={isHomeActive}>|</SeparatorText>
      </LinkSeparator>
      <FooterNavLink
        isDesktop={isDesktop}
        isCurrent={isTermsActive}
        isEventLanding={isEventLanding}
        onPress={() => router.push(ROUTES.TERMS.path)}
        accessibilityLabel="View terms of service"
        useWelcomeHelveticaNeue={isHomeActive}
      >
        Terms
      </FooterNavLink>
      <LinkSeparator accessible={false}>
        <SeparatorText useWelcomeHelveticaNeue={isHomeActive}>|</SeparatorText>
      </LinkSeparator>
      <FooterNavLink
        isDesktop={isDesktop}
        isCurrent={isPrivacyActive}
        isEventLanding={isEventLanding}
        onPress={() => router.push(ROUTES.PRIVACY.path)}
        accessibilityLabel="View privacy policy"
        useWelcomeHelveticaNeue={isHomeActive}
      >
        Privacy Policy
      </FooterNavLink>
      <LinkSeparator accessible={false}>
        <SeparatorText useWelcomeHelveticaNeue={isHomeActive}>|</SeparatorText>
      </LinkSeparator>
      <FooterNavLink
        isDesktop={isDesktop}
        isCurrent={isRouteActive(ROUTES.FAQ.path)}
        isEventLanding={isEventLanding}
        onPress={() => router.push(ROUTES.FAQ.path)}
        accessibilityLabel="View frequently asked questions"
        useWelcomeHelveticaNeue={isHomeActive}
      >
        FAQ
      </FooterNavLink>
      <LinkSeparator accessible={false}>
        <SeparatorText useWelcomeHelveticaNeue={isHomeActive}>|</SeparatorText>
      </LinkSeparator>
      <FooterNavLink
        isDesktop={isDesktop}
        isCurrent={false}
        isEventLanding={isEventLanding}
        onPress={() => Linking.openURL(`mailto:${EMME_SUPPORT_EMAIL}`)}
        accessibilityLabel="Email support"
        useWelcomeHelveticaNeue={isHomeActive}
      >
        Support
      </FooterNavLink>
      <LinkSeparator accessible={false}>
        <SeparatorText useWelcomeHelveticaNeue={isHomeActive}>|</SeparatorText>
      </LinkSeparator>
      <FooterNavLink
        isDesktop={isDesktop}
        isCurrent={false}
        isEventLanding={isEventLanding}
        onPress={applyToBeStylist}
        accessibilityLabel="Apply to be a stylist"
        useWelcomeHelveticaNeue={isHomeActive}
      >
        Apply To Be A Stylist
      </FooterNavLink>
    </>
  );

  const footerSocialIcons = (
    <>
      <FooterSocialIconButton
        isDesktop={isDesktop}
        onPress={() =>
          Linking.openURL('https://www.instagram.com/emmestyling/')
        }
        accessibilityLabel="Emme Styling on Instagram"
      >
        <Icon source={require('@/assets/images/InstagramFooter.png')} />
      </FooterSocialIconButton>
      <FooterSocialIconButton
        isDesktop={isDesktop}
        onPress={() => Linking.openURL('https://www.tiktok.com/@emmestyling_')}
        accessibilityLabel="Emme Styling on TikTok"
      >
        <Icon source={require('@/assets/images/TikTokFooter.png')} />
      </FooterSocialIconButton>
    </>
  );

  return (
    <FooterContainer isDesktop={isDesktop} isEventLanding={isEventLanding}>
      {isDesktop ? (
        <>
          <CopyRightText
            isDesktop={isDesktop}
            useWelcomeHelveticaNeue={isHomeActive}
          >
            © 2026 EMME. All rights reserved.
          </CopyRightText>
          <LinksAndSocialCluster isDesktop={isDesktop}>
            <FlexRow isDesktop={isDesktop}>{footerNavLinks}</FlexRow>
            <SocialRow isDesktop={isDesktop}>{footerSocialIcons}</SocialRow>
          </LinksAndSocialCluster>
        </>
      ) : (
        <>
          <SocialRow isDesktop={isDesktop}>{footerSocialIcons}</SocialRow>
          <FlexRow isDesktop={isDesktop}>{footerNavLinks}</FlexRow>
          <CopyRightText
            isDesktop={isDesktop}
            useWelcomeHelveticaNeue={isHomeActive}
          >
            © 2026 EMME. All rights reserved.
          </CopyRightText>
        </>
      )}
    </FooterContainer>
  );
};

interface FooterLayoutProps {
  isDesktop: boolean;
  isEventLanding?: boolean;
}

const FooterContainer = styled.View<FooterLayoutProps>(
  ({ isDesktop, isEventLanding }) => ({
    flexDirection: isDesktop ? 'row' : 'column',
    justifyContent: isDesktop ? 'space-between' : 'flex-start',
    alignItems: isDesktop ? 'center' : 'center',
    rowGap: isDesktop ? 0 : 14,
    paddingLeft: isDesktop ? 21 : 20,
    paddingRight: isDesktop ? 32 : 20,
    paddingTop: isEventLanding ? 16 : 20,
    paddingBottom: isEventLanding ? 16 : 20,
    marginBottom: 0,
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    backgroundColor: isEventLanding ? sand : green,
  }),
);

const CopyRightText = styled(BodyText)<
  FooterLayoutProps & { useWelcomeHelveticaNeue?: boolean }
>(({ isDesktop, useWelcomeHelveticaNeue }) => ({
  fontSize: 11,
  fontWeight: '300',
  color: mintGreen,
  letterSpacing: -0.12,
  marginBottom: 0,
  lineHeight: 18,
  textAlign: isDesktop ? 'left' : 'center',
  alignSelf: isDesktop ? undefined : 'center',
  flexShrink: 0,
  ...(useWelcomeHelveticaNeue ? FONT_STYLES.welcomeHelveticaNeue : {}),
}));

const FooterNavText = styled(BodyText)<{
  isCurrent: boolean;
  isHovered?: boolean;
  isEventLanding?: boolean;
  useWelcomeHelveticaNeue?: boolean;
}>(
  ({
    isCurrent,
    isHovered,
    isEventLanding = false,
    useWelcomeHelveticaNeue,
  }) => ({
    fontSize: 11,
    fontWeight: isCurrent ? '700' : '300',
    color: footerNavLinkColor(isEventLanding, isHovered),
    letterSpacing: -0.12,
    marginBottom: 0,
    lineHeight: 18,
    ...(useWelcomeHelveticaNeue ? FONT_STYLES.welcomeHelveticaNeue : {}),
  }),
);

const SocialIconWrap = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  width: isDesktop ? 26 : 20,
  height: isDesktop ? 26 : 20,
}));

const Icon = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
});

const LinksAndSocialCluster = styled.View<FooterLayoutProps>(
  ({ isDesktop }) => {
    if (isDesktop) {
      return {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexShrink: 1,
        minWidth: 0,
        columnGap: 20,
      };
    }
    return {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      flexShrink: 0,
      width: '100%',
      rowGap: 12,
    };
  },
);

const FOOTER_LINK_MIN_HEIGHT_DESKTOP = 44;
const FOOTER_LINK_MIN_HEIGHT_MOBILE = 36;
const FOOTER_LINK_PADDING_H = 10;
const FOOTER_LINK_PADDING_V_DESKTOP = 6;
const FOOTER_LINK_PADDING_V_MOBILE = 2;

interface FooterLinkLayoutProps {
  isDesktop: boolean;
}

const FooterLink = styled(TouchableOpacity)<FooterLinkLayoutProps>(
  ({ isDesktop }) => ({
    minHeight: isDesktop
      ? FOOTER_LINK_MIN_HEIGHT_DESKTOP
      : FOOTER_LINK_MIN_HEIGHT_MOBILE,
    justifyContent: 'center',
    paddingVertical: isDesktop
      ? FOOTER_LINK_PADDING_V_DESKTOP
      : FOOTER_LINK_PADDING_V_MOBILE,
    paddingHorizontal: FOOTER_LINK_PADDING_H,
    marginHorizontal: -FOOTER_LINK_PADDING_H,
  }),
);

const FlexRow = styled.View<FooterLayoutProps>(({ isDesktop }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: isDesktop ? 'flex-end' : 'center',
  rowGap: isDesktop ? 8 : 2,
  columnGap: 6,
  flexShrink: 1,
  minWidth: 0,
  width: isDesktop ? undefined : '100%',
}));

const LinkSeparator = styled.View({
  justifyContent: 'center',
  paddingHorizontal: 2,
});

const SeparatorText = styled(BodyText)<{
  useWelcomeHelveticaNeue?: boolean;
}>(({ useWelcomeHelveticaNeue }) => ({
  fontSize: 11,
  color: mintGreen,
  marginBottom: 0,
  lineHeight: 18,
  opacity: 0.85,
  ...(useWelcomeHelveticaNeue ? FONT_STYLES.welcomeHelveticaNeue : {}),
}));

const SocialRow = styled.View<FooterLayoutProps>(({ isDesktop }) => ({
  flexDirection: 'row',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: isDesktop ? undefined : 'center',
  columnGap: 12,
}));

export default Footer;
